// ============= Settings Store (localStorage + Cloud Sync) =============
import { saveSetting, SETTINGS_KEYS } from '@/lib/settingsSync';

export interface CustomStatus {
  id: string;
  key: string;
  nameAr: string;
  nameEn: string;
  color: string;
  order: number;
}

export type UserRole = 'admin' | 'employee' | 'viewer';

export interface AppUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  active: boolean;
  createdAt: string;
}

const STATUSES_KEY = 'custom_statuses';
const USERS_KEY = 'app_users';
const RETURN_STATUSES_KEY = 'return_statuses';
const BRANCHES_KEY = 'branches';

const generateId = () => crypto.randomUUID();

// ---- Default statuses ----
const defaultStatuses: CustomStatus[] = [
  { id: 'default-0', key: 'new_entry', nameAr: 'ادخال جديد', nameEn: 'New Entry', color: 'cyan', order: 0 },
  { id: 'default-1', key: 'in_vehicle', nameAr: 'في المركبة', nameEn: 'In Vehicle', color: 'blue', order: 1 },
  { id: 'default-2', key: 'awaiting_sorting', nameAr: 'بانتظار التصنيف', nameEn: 'Awaiting Sorting', color: 'orange', order: 2 },
  { id: 'default-3', key: 'delivered', nameAr: 'تم التسليم', nameEn: 'Delivered', color: 'emerald', order: 3 },
  { id: 'default-4', key: 'awaiting_driver_approval', nameAr: 'بانتظار موافقة السائق', nameEn: 'Awaiting Driver Approval', color: 'purple', order: 4 },
  { id: 'default-5', key: 'delivery_failed', nameAr: 'تعذر التسليم', nameEn: 'Delivery Failed', color: 'red', order: 5 },
  { id: 'default-6', key: 'postponed', nameAr: 'مؤجل لوقت آخر', nameEn: 'Postponed', color: 'gray', order: 6 },
  { id: 'default-7', key: 'preparing', nameAr: 'قيد التجهيز', nameEn: 'Preparing', color: 'amber', order: 7 },
  { id: 'default-8', key: 'with_customer', nameAr: 'في عهدة العميل', nameEn: 'With Customer', color: 'teal', order: 8 },
  { id: 'default-9', key: 'preparation_cancelled', nameAr: 'إلغاء التجهيز', nameEn: 'Preparation Cancelled', color: 'pink', order: 9 },
  { id: 'default-10', key: 'cancelled', nameAr: 'تم الإلغاء', nameEn: 'Cancelled', color: 'red', order: 10 },
  { id: 'default-11', key: 'rejected_on_arrival', nameAr: 'رفض عند الوصول', nameEn: 'Rejected on Arrival', color: 'orange', order: 11 },
];

// ---- Hidden statuses (not shown in status-change dropdowns) ----
export const HIDDEN_STATUS_KEYS = [
  'new_entry',
  'awaiting_sorting',
  'awaiting_driver_approval',
  'preparing',
  'with_customer',
];

// Statuses where manual status change is completely locked (dropdown disabled, skipped in bulk)
export const LOCKED_STATUS_KEYS = [
  'new_entry', 'preparing', 'with_customer',
  'ادخال جديد', 'قيد التجهيز', 'في عهدة العميل',
];

// Statuses that require a driver to be assigned before changing to them
export const DRIVER_REQUIRED_STATUS_KEYS = [
  'in_vehicle', 'delivered', 'rejected_on_arrival',
  'في المركبة', 'تم التسليم', 'رفض عند الوصول',
];

export const getChangeableStatuses = (): CustomStatus[] =>
  getStatuses().filter(st => !HIDDEN_STATUS_KEYS.includes(st.key));

// ---- Statuses CRUD ----
const STATUSES_VERSION = '5';
const STATUSES_VERSION_KEY = 'custom_statuses_version';

export const getStatuses = (): CustomStatus[] => {
  const version = localStorage.getItem(STATUSES_VERSION_KEY);
  if (version !== STATUSES_VERSION) {
    localStorage.setItem(STATUSES_VERSION_KEY, STATUSES_VERSION);
    saveStatuses(defaultStatuses);
    return defaultStatuses;
  }
  const data = localStorage.getItem(STATUSES_KEY);
  if (!data) {
    saveStatuses(defaultStatuses);
    return defaultStatuses;
  }
  return JSON.parse(data);
};

const saveStatuses = (statuses: CustomStatus[]) => {
  localStorage.setItem(STATUSES_KEY, JSON.stringify(statuses));
  saveSetting(SETTINGS_KEYS.STATUSES, statuses);
};

export const addStatus = (status: Omit<CustomStatus, 'id' | 'order'>): CustomStatus => {
  const statuses = getStatuses();
  const newStatus: CustomStatus = { ...status, id: generateId(), order: statuses.length + 1 };
  statuses.push(newStatus);
  saveStatuses(statuses);
  return newStatus;
};

export const updateStatus = (id: string, data: Partial<Omit<CustomStatus, 'id'>>): CustomStatus | null => {
  const statuses = getStatuses();
  const idx = statuses.findIndex(s => s.id === id);
  if (idx === -1) return null;
  statuses[idx] = { ...statuses[idx], ...data };
  saveStatuses(statuses);
  return statuses[idx];
};

export const deleteStatus = (id: string): boolean => {
  const statuses = getStatuses();
  const filtered = statuses.filter(s => s.id !== id);
  if (filtered.length === statuses.length) return false;
  saveStatuses(filtered);
  return true;
};

// ---- Users CRUD ----
export const getUsers = (): AppUser[] => {
  const data = localStorage.getItem(USERS_KEY);
  if (!data) return [];
  return JSON.parse(data);
};

const saveUsers = (users: AppUser[]) => {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
};

export const addUser = (data: Omit<AppUser, 'id' | 'createdAt'>): AppUser => {
  const users = getUsers();
  const user: AppUser = { ...data, id: generateId(), createdAt: new Date().toISOString() };
  users.push(user);
  saveUsers(users);
  return user;
};

export const updateUser = (id: string, data: Partial<Omit<AppUser, 'id' | 'createdAt'>>): AppUser | null => {
  const users = getUsers();
  const idx = users.findIndex(u => u.id === id);
  if (idx === -1) return null;
  users[idx] = { ...users[idx], ...data };
  saveUsers(users);
  return users[idx];
};

export const deleteUser = (id: string): boolean => {
  const users = getUsers();
  const filtered = users.filter(u => u.id !== id);
  if (filtered.length === users.length) return false;
  saveUsers(filtered);
  return true;
};

// ---- Return Statuses CRUD ----
export interface ReturnStatus {
  id: string;
  key: string;
  nameAr: string;
  nameEn: string;
  color: string;
  order: number;
}

const RETURN_STATUSES_VERSION_KEY = 'return_statuses_version';
const RETURN_STATUSES_VERSION = '3';

const defaultReturnStatuses: ReturnStatus[] = [
  { id: 'ret-1', key: 'has_return', nameAr: 'يوجد ارجاع', nameEn: 'Has Return', color: 'orange', order: 1 },
  { id: 'ret-3', key: 'in_transit', nameAr: 'قيد النقل', nameEn: 'In Transit', color: 'blue', order: 2 },
  { id: 'ret-4', key: 'return_received', nameAr: 'تم استلام الارجاع', nameEn: 'Return Received', color: 'teal', order: 3 },
  { id: 'ret-5', key: 'return_prepared', nameAr: 'تم تجهيز الارجاع', nameEn: 'Return Prepared', color: 'amber', order: 4 },
  { id: 'ret-6', key: 'return_delivered', nameAr: 'تم تسليم الارجاع للعميل', nameEn: 'Return Delivered to Customer', color: 'emerald', order: 5 },
];

const saveReturnStatuses = (statuses: ReturnStatus[]) => {
  localStorage.setItem(RETURN_STATUSES_KEY, JSON.stringify(statuses));
  saveSetting(SETTINGS_KEYS.RETURN_STATUSES, statuses);
};

export const getReturnStatuses = (): ReturnStatus[] => {
  const version = localStorage.getItem(RETURN_STATUSES_VERSION_KEY);
  if (version !== RETURN_STATUSES_VERSION) {
    localStorage.setItem(RETURN_STATUSES_VERSION_KEY, RETURN_STATUSES_VERSION);
    saveReturnStatuses(defaultReturnStatuses);
    return defaultReturnStatuses;
  }
  const data = localStorage.getItem(RETURN_STATUSES_KEY);
  if (!data) {
    saveReturnStatuses(defaultReturnStatuses);
    return defaultReturnStatuses;
  }
  return JSON.parse(data);
};

export const addReturnStatus = (status: Omit<ReturnStatus, 'id' | 'order'>): ReturnStatus => {
  const statuses = getReturnStatuses();
  const newStatus: ReturnStatus = { ...status, id: generateId(), order: statuses.length + 1 };
  statuses.push(newStatus);
  saveReturnStatuses(statuses);
  return newStatus;
};

export const updateReturnStatus = (id: string, data: Partial<Omit<ReturnStatus, 'id'>>): ReturnStatus | null => {
  const statuses = getReturnStatuses();
  const idx = statuses.findIndex(s => s.id === id);
  if (idx === -1) return null;
  statuses[idx] = { ...statuses[idx], ...data };
  saveReturnStatuses(statuses);
  return statuses[idx];
};

export const deleteReturnStatus = (id: string): boolean => {
  const statuses = getReturnStatuses();
  const filtered = statuses.filter(s => s.id !== id);
  if (filtered.length === statuses.length) return false;
  saveReturnStatuses(filtered);
  return true;
};

// ---- Branches CRUD ----
export interface Branch {
  id: string;
  name: string;
  city: string;
  address: string;
  phone: string;
}

const defaultBranches: Branch[] = [
  { id: 'branch-1', name: 'الفرع الرئيسي', city: '', address: '', phone: '' },
];

const saveBranches = (branches: Branch[]) => {
  localStorage.setItem(BRANCHES_KEY, JSON.stringify(branches));
  saveSetting(SETTINGS_KEYS.BRANCHES, branches);
};

export const getBranches = (): Branch[] => {
  const data = localStorage.getItem(BRANCHES_KEY);
  if (!data) {
    saveBranches(defaultBranches);
    return defaultBranches;
  }
  return JSON.parse(data);
};

export const addBranch = (branch: Omit<Branch, 'id'>): Branch => {
  const branches = getBranches();
  const newBranch: Branch = { ...branch, id: generateId() };
  branches.push(newBranch);
  saveBranches(branches);
  return newBranch;
};

export const updateBranch = (id: string, data: Partial<Omit<Branch, 'id'>>): Branch | null => {
  const branches = getBranches();
  const idx = branches.findIndex(b => b.id === id);
  if (idx === -1) return null;
  branches[idx] = { ...branches[idx], ...data };
  saveBranches(branches);
  return branches[idx];
};

export const deleteBranch = (id: string): boolean => {
  const branches = getBranches();
  const filtered = branches.filter(b => b.id !== id);
  if (filtered.length === branches.length) return false;
  saveBranches(filtered);
  return true;
};

// ---- General Settings ----
const GENERAL_SETTINGS_KEY = 'general_settings';

export interface GeneralSettings {
  aiAssistantEnabled: boolean;
  messagingEnabled: boolean;
  companyLogo?: string | null;
  sidebarLogo?: string | null;
  sidebarLogoSize?: number;
}

const defaultGeneralSettings: GeneralSettings = {
  aiAssistantEnabled: false,
  messagingEnabled: false,
  sidebarLogoSize: 100,
};

export const getGeneralSettings = (): GeneralSettings => {
  const data = localStorage.getItem(GENERAL_SETTINGS_KEY);
  if (!data) return defaultGeneralSettings;
  return { ...defaultGeneralSettings, ...JSON.parse(data) };
};

export const saveGeneralSettings = (settings: GeneralSettings) => {
  localStorage.setItem(GENERAL_SETTINGS_KEY, JSON.stringify(settings));
  saveSetting(SETTINGS_KEYS.GENERAL, settings);
  window.dispatchEvent(new Event('general-settings-changed'));
};

// ---- Tracking Number Config ----
const TRACKING_CONFIG_KEY = 'tracking_config';

export interface TrackingNumberConfig {
  shipmentPrefix: string;
  shipmentDigits: number;
  shipmentNextSeq: number;
  returnReceiptPrefix: string;
  returnReceiptDigits: number;
  returnReceiptNextSeq: number;
  disbursementPrefix: string;
  disbursementDigits: number;
  disbursementNextSeq: number;
  bundlePrefix: string;
  bundleDigits: number;
  bundleNextSeq: number;
  useSeparator: boolean;
}

const defaultTrackingConfig: TrackingNumberConfig = {
  shipmentPrefix: 'SHP',
  shipmentDigits: 6,
  shipmentNextSeq: 1,
  returnReceiptPrefix: 'RR',
  returnReceiptDigits: 6,
  returnReceiptNextSeq: 1,
  disbursementPrefix: 'DV',
  disbursementDigits: 6,
  disbursementNextSeq: 1,
  bundlePrefix: 'BDL',
  bundleDigits: 6,
  bundleNextSeq: 1,
  useSeparator: true,
};

export const getTrackingConfig = (): TrackingNumberConfig => {
  const data = localStorage.getItem(TRACKING_CONFIG_KEY);
  if (!data) return defaultTrackingConfig;
  return { ...defaultTrackingConfig, ...JSON.parse(data) };
};

export const saveTrackingConfig = async (config: TrackingNumberConfig) => {
  localStorage.setItem(TRACKING_CONFIG_KEY, JSON.stringify(config));
  saveSetting(SETTINGS_KEYS.TRACKING_CONFIG, config);
  
  // Sync to DB tracking_sequences table
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    const updates = [
      { type: 'shipment', prefix: config.shipmentPrefix, digits: config.shipmentDigits, next_seq: config.shipmentNextSeq },
      { type: 'returnReceipt', prefix: config.returnReceiptPrefix, digits: config.returnReceiptDigits, next_seq: config.returnReceiptNextSeq },
      { type: 'disbursement', prefix: config.disbursementPrefix, digits: config.disbursementDigits, next_seq: config.disbursementNextSeq },
      { type: 'bundle', prefix: config.bundlePrefix, digits: config.bundleDigits, next_seq: config.bundleNextSeq },
    ];
    for (const u of updates) {
      await (supabase.from('tracking_sequences') as any).update({ prefix: u.prefix, digits: u.digits, next_seq: u.next_seq }).eq('type', u.type);
    }
  } catch (err) {
    console.error('Failed to sync tracking config to DB:', err);
  }
};

export const generateSequentialNumber = (prefix: string, digits: number, seq: number, useSeparator: boolean = true): string => {
  const padded = String(seq).padStart(digits, '0');
  if (!prefix) return padded;
  return `${prefix}${useSeparator ? '-' : ''}${padded}`;
};

export const incrementTrackingSeq = async (type: 'shipment' | 'returnReceipt' | 'disbursement' | 'bundle'): Promise<string> => {
  const config = getTrackingConfig();
  const sep = config.useSeparator !== false;

  const applySeparator = (trackingNum: string): string => {
    if (sep) return trackingNum; // DB already includes '-'
    // Remove the first '-' that the DB function adds between prefix and number
    return trackingNum.replace('-', '');
  };

  try {
    const { supabase } = await import('@/integrations/supabase/client');
    const { data, error } = await supabase.rpc('next_tracking_number', { p_type: type });
    if (error) throw error;
    return applySeparator(data as string);
  } catch (err) {
    console.error('DB tracking number failed, using localStorage fallback:', err);
    let number: string;
    if (type === 'shipment') {
      number = generateSequentialNumber(config.shipmentPrefix, config.shipmentDigits, config.shipmentNextSeq, sep);
      config.shipmentNextSeq++;
    } else if (type === 'returnReceipt') {
      number = generateSequentialNumber(config.returnReceiptPrefix, config.returnReceiptDigits, config.returnReceiptNextSeq, sep);
      config.returnReceiptNextSeq++;
    } else if (type === 'bundle') {
      number = generateSequentialNumber(config.bundlePrefix, config.bundleDigits, config.bundleNextSeq, sep);
      config.bundleNextSeq++;
    } else {
      number = generateSequentialNumber(config.disbursementPrefix, config.disbursementDigits, config.disbursementNextSeq, sep);
      config.disbursementNextSeq++;
    }
    saveTrackingConfig(config);
    return number;
  }
};
